// =============================================================================
// PeakHoursHeatmap - Mappa densità appuntamenti giorno × ora ("ore di punta")
// =============================================================================

import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Loader2, CalendarClock } from 'lucide-react';
import { EmptyState } from '../common';
import type { PeakHoursResult } from '../../api/hooks';

// Scala colore discreta (stile "contribution graph"): vuoto → emerald crescente
const SCALE = ['#f3f4f6', '#d1fae5', '#6ee7b7', '#10b981', '#047857']; // gray-100 → emerald-700

function levelFor(count: number, max: number): number {
  if (count <= 0 || max <= 0) return 0;
  const ratio = count / max;
  if (ratio <= 0.25) return 1;
  if (ratio <= 0.5) return 2;
  if (ratio <= 0.75) return 3;
  return 4;
}

interface Props {
  data?: PeakHoursResult;
  loading?: boolean;
}

export function PeakHoursHeatmap({ data, loading }: Props) {
  const { t, i18n } = useTranslation('reports');

  // Nomi brevi dei giorni (lun→dom) rispettando la lingua attiva
  const dayLabels = useMemo(() => {
    const fmt = new Intl.DateTimeFormat(i18n.language === 'it' ? 'it-IT' : 'en-US', {
      weekday: 'short',
    });
    // 2024-01-01 è un lunedì
    return Array.from({ length: 7 }, (_, i) =>
      fmt.format(new Date(2024, 0, 1 + i)),
    );
  }, [i18n.language]);

  if (loading) {
    return (
      <div className="h-[300px] flex items-center justify-center">
        <Loader2 className="w-6 h-6 animate-spin text-emerald-500" />
      </div>
    );
  }

  if (!data || data.totalAppointments === 0) {
    return (
      <EmptyState
        icon={CalendarClock}
        title={t('peak.noData')}
        description={t('peak.noDataHint')}
        className="py-16"
      />
    );
  }

  const { matrix, hourStart, hourEnd, maxCount } = data;
  const hours = Array.from({ length: hourEnd - hourStart + 1 }, (_, i) => hourStart + i);

  return (
    <div>
      <div className="overflow-x-auto">
        <div className="inline-block min-w-full">
          {/* Riga intestazione ore */}
          <div className="flex">
            <div className="w-12 flex-shrink-0" />
            {hours.map((h) => (
              <div
                key={h}
                className="flex-1 min-w-[28px] text-center text-[11px] text-gray-400 font-medium pb-1.5 tabular-nums"
              >
                {String(h).padStart(2, '0')}
              </div>
            ))}
          </div>

          {/* Righe giorni */}
          {matrix.map((row, dayIdx) => (
            <div key={dayIdx} className="flex items-center">
              <div className="w-12 flex-shrink-0 text-xs text-gray-500 font-medium capitalize pr-2 text-right">
                {dayLabels[dayIdx]}
              </div>
              {row.map((count, i) => {
                const level = levelFor(count, maxCount);
                const hour = hours[i];
                return (
                  <div key={i} className="flex-1 min-w-[28px] p-0.5">
                    <div
                      className="aspect-square rounded-[4px] flex items-center justify-center text-[10px] font-semibold transition-transform hover:scale-110 hover:ring-2 hover:ring-emerald-300 cursor-default"
                      style={{
                        backgroundColor: SCALE[level],
                        color: level >= 3 ? '#ffffff' : level === 0 ? 'transparent' : '#374151',
                      }}
                      title={`${dayLabels[dayIdx]} ${String(hour).padStart(2, '0')}:00 — ${count} ${t('peak.appointments')}`}
                    >
                      {count > 0 ? count : ''}
                    </div>
                  </div>
                );
              })}
            </div>
          ))}
        </div>
      </div>

      {/* Legenda scala */}
      <div className="flex items-center justify-end gap-2 mt-4 text-xs text-gray-400">
        <span>{t('peak.less')}</span>
        {SCALE.map((c, i) => (
          <span
            key={i}
            className="w-3.5 h-3.5 rounded-[3px] border border-gray-200"
            style={{ backgroundColor: c }}
          />
        ))}
        <span>{t('peak.more')}</span>
      </div>
    </div>
  );
}

export default PeakHoursHeatmap;
